import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, HeartPulse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings, assetUrl } from "@/context/SettingsContext";
import ThemeToggle from "@/components/ThemeToggle";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/courses", label: "Courses" },
  { to: "/facilities", label: "Facilities" },
  { to: "/placements", label: "Clinical Training" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const brand = settings?.branding || {};
  const shortName = brand.shortName || "Ambigara";
  const subtitle = brand.navSubtitle || "College of Nursing";
  const logoDark = brand.logoDark && brand.showLogoImage !== false ? assetUrl(brand.logoDark) : "";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-colors duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-[0_2px_18px_rgba(15,23,42,0.05)]"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="site-logo" className="flex items-center gap-3 group">
          {logoDark ? (
            <img src={logoDark} alt={shortName} className="h-11 w-auto object-contain group-hover:scale-105 transition-transform" />
          ) : (
            <span className="w-11 h-11 rounded-xl grid place-items-center shadow-md group-hover:scale-105 transition-transform brand-primary-bg text-white">
              <HeartPulse className="w-5 h-5" />
            </span>
          )}
          <span className="leading-tight hidden sm:block">
            <span className="block font-heading text-lg text-slate-900 font-semibold">{shortName}</span>
            <span className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">{subtitle}</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive ? "brand-primary-text bg-slate-100" : "text-slate-700 hover:text-slate-900 hover:bg-slate-50"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <ThemeToggle compact dataTestid="header-theme-toggle" />
          <Link to="/contact#inquiry" data-testid="apply-now-btn-header">
            <Button
              style={{ backgroundColor: "var(--brand-primary)" }}
              className="rounded-full text-white px-6 h-11 font-medium shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:brightness-110 transition-all"
            >
              Apply Now
            </Button>
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <ThemeToggle compact dataTestid="header-theme-toggle-mobile" />
          <button
            data-testid="mobile-menu-toggle"
            className="p-2 rounded-lg text-slate-700 hover:bg-slate-100"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive ? "brand-primary-text bg-slate-100" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/contact#inquiry" className="mt-2" data-testid="apply-now-btn-mobile">
              <Button
                style={{ backgroundColor: "var(--brand-primary)" }}
                className="w-full rounded-full text-white h-11"
              >
                Apply Now
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
